/**
 * ─────────────────────────────────────────────────────────────────
 * Pantalla de Tickets (AdminOCR).
 *
 * Diferencias móvil vs web:
 *  - Móvil: botón de cámara nativa (@capacitor/camera) en vez de input file
 *  - Móvil: "Ver ticket" abre con @capacitor/share (visor nativo del dispositivo)
 *  - Móvil: confirmación de eliminar con bottom sheet (no window.confirm)
 *  - Móvil: card de subida compacta (no sticky lateral, sino arriba)
 *  - Web: comportamiento original intacto
 * ─────────────────────────────────────────────────────────────────
 */
import React, { useState, useEffect } from "react";
import {
    Box, Typography, Button, Stack, Card, CardContent,
    TextField, MenuItem, Grid, IconButton, CircularProgress,
    Tooltip, Alert, useTheme, useMediaQuery, Slide, Dialog,
    Chip, Divider,
} from "@mui/material";
import CloudUploadIcon  from "@mui/icons-material/CloudUpload";
import DeleteIcon       from "@mui/icons-material/Delete";
import VisibilityIcon   from "@mui/icons-material/Visibility";
import ReceiptLongIcon  from "@mui/icons-material/ReceiptLong";
import PhotoCameraIcon  from "@mui/icons-material/PhotoCamera";
import CancelIcon       from "@mui/icons-material/Cancel";
import { Capacitor }    from "@capacitor/core";
import { obtenerListasDelUsuario }              from "../api/listas";
import { subirTicket, obtenerTickets, eliminarTicket } from "../api/tickets";

/** Transición bottom sheet */
const SlideUp = (props) => <Slide direction="up" {...props} />;

/**
 * Base URL para construir URLs de archivos del backend.
 * En nativo apunta a Tailscale; en web usa rutas relativas.
 */
const getApiBase = () => {
    if (Capacitor.isNativePlatform()) {
        return process.env.NODE_ENV === "production"
            ? "https://tahuya.taile75a0b.ts.net"
            : "http://192.168.1.50:8000";
    }
    return "";
};

export default function MisTickets() {
    const theme    = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const esNativo = Capacitor.isNativePlatform();

    const [tickets,   setTickets]   = useState([]);
    const [listas,    setListas]    = useState([]);
    const [loading,   setLoading]   = useState(true);
    const [subiendo,  setSubiendo]  = useState(false);
    const [file,                setFile]                = useState(null);  // File (web) o base64 (nativo)
    const [esBase64,            setEsBase64]            = useState(false); // true si viene de cámara
    const [nombreImg,           setNombreImg]           = useState("");    // nombre original
    const [nombrePersonalizado, setNombrePersonalizado] = useState("");    // nombre editable por el usuario
    const [idLista,             setIdLista]             = useState("");
    const [error,     setError]     = useState(null);
    const [success,   setSuccess]   = useState(null);

    // Estado para el bottom sheet de confirmación de eliminar
    const [ticketAEliminar, setTicketAEliminar] = useState(null);

    useEffect(() => { loadData(); }, []);

    /** Carga tickets y listas del usuario */
    const loadData = async () => {
        setLoading(true);
        try {
            const [ticketsData, listasData] = await Promise.all([
                obtenerTickets(),
                obtenerListasDelUsuario(),
            ]);
            setTickets(ticketsData);
            setListas(listasData);
        } catch (err) {
            console.error("Error cargando datos:", err);
            setError("No se pudieron cargar los tickets.");
        } finally {
            setLoading(false);
        }
    };

    /** Selector de archivo web (input file normal) */
    const onPick = (e) => {
        const f = e.target.files?.[0];
        setFile(f || null);
        setEsBase64(false);
        setNombreImg(f?.name || "");
        setNombrePersonalizado(f ? f.name.replace(/\.[^.]+$/, "") : "");
        setError(null);
        setSuccess(null);
    };

    /**
     * Abre cámara o galería nativa via Capacitor.
     * El resultado es un base64 que luego se convierte a Blob para subirlo.
     */
    const onCamaraNativa = async () => {
        try {
            const { Camera, CameraSource, CameraResultType } = await import("@capacitor/camera");
            const image = await Camera.getPhoto({
                quality:      85,
                allowEditing: false,
                resultType:   CameraResultType.Base64,
                source:       CameraSource.Prompt, // pregunta: cámara o galería
            });
            if (!image.base64String) return;
            setFile(image.base64String);
            setEsBase64(true);
            setNombreImg("ticket_foto.jpg");
            setNombrePersonalizado("");
            setError(null);
            setSuccess(null);
        } catch (err) {
            // El usuario canceló — no es error real
            if (!err?.message?.includes("cancel")) {
                setError("No se pudo acceder a la cámara.");
            }
        }
    };

    /** Sube el ticket al backend (web o nativo) */
    const onUpload = async () => {
        if (!file) return setError("Selecciona un archivo primero.");
        setError(null);
        setSuccess(null);
        setSubiendo(true);

        try {
            let fileToUpload;
            // Nombre final: el que escribió el usuario o el original, con extensión correcta
            const ext        = esBase64 ? ".jpg" : (file.name.match(/\.[^.]+$/) || [""])[0];
            const nombreFinal = (nombrePersonalizado.trim() || (esBase64 ? "ticket" : file.name.replace(/\.[^.]+$/, ""))) + ext;

            if (esBase64) {
                // Convierte base64 a File para la API
                const res  = await fetch(`data:image/jpeg;base64,${file}`);
                const blob = await res.blob();
                fileToUpload = new File([blob], nombreFinal, { type: "image/jpeg" });
            } else {
                // Renombra el File con el nombre personalizado
                fileToUpload = new File([file], nombreFinal, { type: file.type });
            }

            await subirTicket(fileToUpload, idLista || null);
            setSuccess("Ticket guardado correctamente.");
            setFile(null);
            setEsBase64(false);
            setNombreImg("");
            setNombrePersonalizado("");
            setIdLista("");
            // Limpiar input file en web
            const input = document.getElementById("ticket-file-input");
            if (input) input.value = "";
            loadData();
        } catch (err) {
            setError(err.response?.data?.detail || "Error al subir el ticket.");
        } finally {
            setSubiendo(false);
        }
    };

    /**
     * Ver ticket.
     * - Web: abre en nueva pestaña
     * - Nativo: descarga y abre con Share nativo del dispositivo
     */
    const handleVer = async (t) => {
        const url = getFileUrl(t.ruta_archivo);
        if (!esNativo) {
            window.open(url, "_blank");
            return;
        }
        try {
            const { Filesystem, Directory, Encoding } = await import("@capacitor/filesystem");
            const { Share } = await import("@capacitor/share");
            // Descarga el archivo
            const res      = await fetch(url, {
                headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
            });
            const blob     = await res.blob();
            const base64   = await blobToBase64(blob);
            const fileName = t.nombre_archivo;
            // Guarda temporalmente en el dispositivo
            await Filesystem.writeFile({
                path:      fileName,
                data:      base64.split(",")[1],
                directory: Directory.Cache,
            });
            const { uri } = await Filesystem.getUri({ path: fileName, directory: Directory.Cache });
            // Abre con el visor nativo
            await Share.share({ title: fileName, url: uri, dialogTitle: "Abrir ticket" });
        } catch (err) {
            console.error("Error abriendo ticket:", err);
            // Fallback: abrir en navegador
            window.open(url, "_blank");
        }
    };

    /** Convierte Blob a base64 */
    const blobToBase64 = (blob) => new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onload  = () => res(reader.result);
        reader.onerror = rej;
        reader.readAsDataURL(blob);
    });

    /** Confirma eliminación (web: confirm nativo, móvil: bottom sheet) */
    const handleEliminar = async () => {
        if (!ticketAEliminar) return;
        try {
            await eliminarTicket(ticketAEliminar.idticket);
            setTickets(tickets.filter(t => t.idticket !== ticketAEliminar.idticket));
            setTicketAEliminar(null);
        } catch {
            setError("Error al eliminar el ticket.");
            setTicketAEliminar(null);
        }
    };

    /** Solicita confirmación según plataforma */
    const pedirConfirmacionEliminar = (t) => {
        if (isMobile) {
            // Móvil: bottom sheet
            setTicketAEliminar(t);
        } else {
            // Web: confirm nativo
            if (window.confirm(`¿Eliminar el ticket "${t.nombre_archivo}"?`)) {
                setTicketAEliminar(t);
                handleEliminar();
            }
        }
    };

    /** Construye URL absoluta del archivo */
    const getFileUrl = (ruta) => {
        if (!ruta) return "";
        if (ruta.startsWith("http")) return ruta;
        return `${getApiBase()}${ruta}`;
    };

    /* ── Panel de subida ────────────────────────────────────────── */
    const panelSubida = (
        <Card sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: isMobile ? "14px !important" : "20px !important" }}>
                <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ fontWeight: 800, mb: isMobile ? 1.5 : 2 }}>
                    Guardar nuevo ticket
                </Typography>

                <Stack spacing={isMobile ? 1.5 : 2.5}>
                    {/*
                     * En nativo: botón que abre cámara/galería via Capacitor.
                     * En web: input file normal.
                     */}
                    {esNativo ? (
                        <Button
                            variant="outlined"
                            startIcon={<PhotoCameraIcon />}
                            fullWidth
                            onClick={onCamaraNativa}
                            sx={{
                                borderStyle:   "dashed",
                                borderRadius:  2,
                                minHeight:     48,
                                fontWeight:    700,
                                textTransform: "none",
                                justifyContent: "flex-start",
                                color: nombreImg ? "primary.main" : "text.secondary",
                            }}
                        >
                            {nombreImg || "Cámara o galería"}
                        </Button>
                    ) : (
                        <Button
                            variant="outlined"
                            component="label"
                            startIcon={<CloudUploadIcon />}
                            fullWidth
                            sx={{ py: 1.5, borderStyle: "dashed", borderRadius: 2, textTransform: "none" }}
                        >
                            {file ? file.name : "Seleccionar archivo"}
                            <input
                                id="ticket-file-input"
                                type="file"
                                hidden
                                accept="image/*,.pdf"
                                onChange={onPick}
                            />
                        </Button>
                    )}

                    {/* Campo de nombre editable — visible cuando hay archivo seleccionado */}
                    {file && (
                        <TextField
                            label="Nombre del ticket"
                            value={nombrePersonalizado}
                            onChange={(e) => setNombrePersonalizado(e.target.value)}
                            fullWidth
                            size="small"
                            placeholder="Ej: Mercadona 18 abril"
                            inputProps={{ maxLength: 60 }}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        />
                    )}

                    <TextField
                        select
                        label="Asociar a una lista (opcional)"
                        value={idLista}
                        onChange={(e) => setIdLista(e.target.value)}
                        fullWidth
                        size="small"
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    >
                        <MenuItem value=""><em>Ninguna</em></MenuItem>
                        {listas.map((l) => (
                            <MenuItem key={l.idlista} value={l.idlista}>{l.nombre}</MenuItem>
                        ))}
                    </TextField>

                    <Button
                        variant="contained"
                        onClick={onUpload}
                        disabled={subiendo || !file}
                        fullWidth
                        sx={{ borderRadius: 2, minHeight: 44, fontWeight: 700, textTransform: "none" }}
                    >
                        {subiendo ? <CircularProgress size={22} color="inherit" /> : "Guardar en mi caja"}
                    </Button>

                    {error   && <Alert severity="error"   sx={{ borderRadius: 2 }}>{error}</Alert>}
                    {success && <Alert severity="success" sx={{ borderRadius: 2 }}>{success}</Alert>}
                </Stack>
            </CardContent>
        </Card>
    );

    return (
        <Box sx={{ p: { xs: 1.5, md: 3 }, maxWidth: 1000, mx: "auto" }}>

            {/* ── HEADER ──────────────────────────────────────────────── */}
            <Stack direction="row" spacing={isMobile ? 1 : 2} alignItems="center" sx={{ mb: isMobile ? 2 : 4 }}>
                <ReceiptLongIcon color="primary" sx={{ fontSize: isMobile ? 28 : 40 }} />
                <Box>
                    <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 900 }}>
                        Mis tickets
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.7 }}>
                        Guarda tus tickets y capturas como recordatorio.
                    </Typography>
                </Box>
            </Stack>

            {/*
             * Móvil: panel de subida arriba, lista debajo (vertical)
             * Escritorio: panel lateral izquierdo sticky, lista a la derecha
             */}
            {isMobile ? (
                <Stack spacing={2}>
                    {panelSubida}

                    {/* Lista de tickets en móvil */}
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                        Tickets guardados ({tickets.length})
                    </Typography>

                    {loading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : tickets.length === 0 ? (
                        <Card sx={{ borderRadius: 3, border: "1px dashed rgba(0,0,0,0.1)" }}>
                            <CardContent sx={{ textAlign: "center", py: 5 }}>
                                <ReceiptLongIcon sx={{ fontSize: 48, opacity: 0.2, mb: 1 }} />
                                <Typography variant="body2" sx={{ opacity: 0.6 }}>
                                    Aún no has guardado ningún ticket.
                                </Typography>
                            </CardContent>
                        </Card>
                    ) : (
                        <Stack spacing={1.5}>
                            {tickets.map((t) => (
                                <TicketCard
                                    key={t.idticket}
                                    ticket={t}
                                    isMobile={isMobile}
                                    onVer={() => handleVer(t)}
                                    onEliminar={() => pedirConfirmacionEliminar(t)}
                                    getFileUrl={getFileUrl}
                                />
                            ))}
                        </Stack>
                    )}
                </Stack>
            ) : (
                /* Escritorio: grid lateral */
                <Grid container spacing={4}>
                    <Grid item xs={12} md={4}>
                        <Box sx={{ position: "sticky", top: 20 }}>{panelSubida}</Box>
                    </Grid>
                    <Grid item xs={12} md={8}>
                        <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
                            Tickets guardados ({tickets.length})
                        </Typography>
                        {loading ? (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : tickets.length === 0 ? (
                            <Card sx={{ borderRadius: 3, bgcolor: "rgba(0,0,0,0.02)", border: "1px dashed rgba(0,0,0,0.1)" }}>
                                <CardContent sx={{ textAlign: "center", py: 8 }}>
                                    <ReceiptLongIcon sx={{ fontSize: 60, opacity: 0.2, mb: 2 }} />
                                    <Typography variant="body1" sx={{ opacity: 0.6 }}>
                                        Aún no has guardado ningún ticket.
                                    </Typography>
                                </CardContent>
                            </Card>
                        ) : (
                            <Grid container spacing={2}>
                                {tickets.map((t) => (
                                    <Grid item xs={12} sm={6} key={t.idticket}>
                                        <TicketCard
                                            ticket={t}
                                            isMobile={false}
                                            onVer={() => handleVer(t)}
                                            onEliminar={() => pedirConfirmacionEliminar(t)}
                                            getFileUrl={getFileUrl}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            )}

            {/* ── BOTTOM SHEET CONFIRMAR ELIMINAR (solo móvil) ────────── */}
            <Dialog
                open={Boolean(ticketAEliminar) && isMobile}
                onClose={() => setTicketAEliminar(null)}
                TransitionComponent={SlideUp}
                fullWidth
                PaperProps={{
                    sx: {
                        position:     "fixed",
                        bottom:       0, left: 0, right: 0,
                        m:            0,
                        width:        "100%",
                        maxWidth:     "100%",
                        borderRadius: "20px 20px 0 0",
                        pb:           "env(safe-area-inset-bottom)",
                    },
                }}
                sx={{ "& .MuiDialog-container": { alignItems: "flex-end" } }}
            >
                <Box sx={{ display: "flex", justifyContent: "center", pt: 1.5 }}>
                    <Box sx={{ width: 40, height: 4, borderRadius: 99, bgcolor: "divider" }} />
                </Box>
                <Box sx={{ px: 2.5, pt: 1.5, pb: 3 }}>
                    <Typography variant="h6" color="error" sx={{ fontWeight: 800, mb: 0.5 }}>
                        Eliminar ticket
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                        ¿Seguro que quieres eliminar <strong>{ticketAEliminar?.nombre_archivo}</strong>?
                        Esta acción no se puede deshacer.
                    </Typography>
                    <Stack direction="row" spacing={1.5}>
                        <Button
                            fullWidth variant="outlined"
                            startIcon={<CancelIcon />}
                            onClick={() => setTicketAEliminar(null)}
                            sx={{ borderRadius: 2, minHeight: 48, fontWeight: 700, textTransform: "none" }}
                        >
                            Cancelar
                        </Button>
                        <Button
                            fullWidth variant="contained" color="error"
                            startIcon={<DeleteIcon />}
                            onClick={handleEliminar}
                            sx={{ borderRadius: 2, minHeight: 48, fontWeight: 700, textTransform: "none" }}
                        >
                            Eliminar
                        </Button>
                    </Stack>
                </Box>
            </Dialog>
        </Box>
    );
}

/* ─────────────────────────────────────────────────────────────────
 * TicketCard
 * Card individual de ticket — compacta en móvil, completa en escritorio.
 * ───────────────────────────────────────────────────────────────── */
function TicketCard({ ticket: t, isMobile, onVer, onEliminar, getFileUrl }) {
    const esImagen = ["jpg", "jpeg", "png"].includes(t.tipo_archivo);

    return (
        <Card sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: isMobile ? "12px !important" : "16px !important" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        {/* Nombre del archivo */}
                        <Typography
                            variant={isMobile ? "body2" : "subtitle1"}
                            sx={{ fontWeight: 700, mb: 0.25 }}
                            noWrap
                        >
                            {t.nombre_archivo}
                        </Typography>

                        {/* Fecha */}
                        <Typography variant="caption" display="block" color="text.secondary">
                            Subido el: {new Date(t.fecha_subida).toLocaleDateString()}
                        </Typography>

                        {/* Lista asociada */}
                        {t.lista_nombre && (
                            <Typography variant="caption" sx={{ color: "primary.main", fontWeight: 600 }}>
                                Lista: {t.lista_nombre}
                            </Typography>
                        )}
                    </Box>

                    {/* Botones acción */}
                    <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0, ml: 1 }}>
                        <Tooltip title="Ver" arrow>
                            <IconButton
                                size="small"
                                color="primary"
                                onClick={onVer}
                                sx={{ minWidth: 36, minHeight: 36 }}
                            >
                                <VisibilityIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar" arrow>
                            <IconButton
                                size="small"
                                color="error"
                                onClick={onEliminar}
                                sx={{ minWidth: 36, minHeight: 36 }}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Stack>

                {/* Preview imagen — solo en escritorio para mantener la card compacta en móvil */}
                {!isMobile && esImagen && (
                    <Box
                        sx={{
                            mt: 1.5, height: 120, borderRadius: 2,
                            bgcolor:            "rgba(0,0,0,0.05)",
                            backgroundImage:    `url(${getFileUrl(t.ruta_archivo)})`,
                            backgroundSize:     "cover",
                            backgroundPosition: "center",
                        }}
                    />
                )}
                {!isMobile && t.tipo_archivo === "pdf" && (
                    <Box sx={{ mt: 1.5, height: 80, borderRadius: 2, bgcolor: "rgba(0,0,0,0.05)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Typography variant="button" color="text.secondary">PDF</Typography>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
}